/**
 * REVOLUTIONARY 2-SCREEN ONBOARDING SYSTEM - REACT WEB VERSION
 * 
 * Replicated from proven Flutter mobile design with MONKEY-SIMPLE worker-first excellence
 * Two screens: Workplace Setup + Profile Completion (identical to Flutter architecture)
 * JOEBOB MCALLIHAN QUALITY STANDARDS: Professional power with kindergarten simplicity
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { 
  Building, 
  MapPin, 
  Users, 
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  DollarSign,
  User,
  AlertTriangle
} from 'lucide-react';

interface OnboardingProps {
  onComplete: () => void;
}

export function Onboarding({ onComplete }: OnboardingProps) {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasExistingProgress, setHasExistingProgress] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');

  // Workplace setup state (Screen 1)
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedZipCode, setSelectedZipCode] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [detectedUnion, setDetectedUnion] = useState('');
  const [hasUnion, setHasUnion] = useState(false);
  const [selectedUnionChapter, setSelectedUnionChapter] = useState('');
  
  // Pay information state (Screen 2)
  const [payFrequency, setPayFrequency] = useState('weekly');
  const [hourlyRate, setHourlyRate] = useState('');
  const [salaryAmount, setSalaryAmount] = useState('');
  
  // UI state
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [locations, setLocations] = useState<string[]>([]);

  // Mock data matching Flutter implementation
  const companies = [
    {
      id: '1',
      name: 'UPS',
      industry: 'Logistics',
      union_status: true,
      union_name: 'Teamsters Local 804',
      locations: {
        '10001': ['UPS Customer Center - Manhattan', 'UPS Store #1234'],
        '10002': ['UPS Distribution Center', 'UPS Air Hub'],
        '11101': ['UPS Queens Facility'],
      }
    },
    {
      id: '2',
      name: 'Amazon',
      industry: 'E-commerce',
      union_status: false,
      union_name: null,
      locations: {
        '60601': ['Amazon Fulfillment Center CHI1', 'Amazon Fresh Warehouse'],
        '60602': ['Amazon Delivery Station', 'Amazon Locker Hub'],
        '60603': ['Amazon Prime Now'],
      }
    },
    {
      id: '3',
      name: 'Starbucks',
      industry: 'Food Service',
      union_status: true,
      union_name: 'Workers United',
      locations: {
        '98101': ['Starbucks Pike Place', 'Starbucks Reserve Roastery'],
        '98102': ['Starbucks Capitol Hill', 'Starbucks University Way'],
        '98103': ['Starbucks Fremont', 'Starbucks Green Lake'],
      }
    },
    {
      id: '4',
      name: 'Walmart',
      industry: 'Retail',
      union_status: false,
      union_name: null,
      locations: {
        '90210': ['Walmart Supercenter Beverly Hills', 'Walmart Neighborhood Market'],
        '90211': ['Walmart Express'],
        '90212': ['Walmart Pharmacy'],
      }
    },
    {
      id: '5',
      name: 'Target',
      industry: 'Retail',
      union_status: false,
      union_name: null,
      locations: {
        '55401': ['Target Store Minneapolis', 'Target Express'],
        '55402': ['Target SuperTarget', 'Target Pharmacy'],
      }
    },
    {
      id: '6',
      name: 'Home Depot',
      industry: 'Retail',
      union_status: false,
      union_name: null,
      locations: {
        '30309': ['Home Depot Store #1234', 'Home Depot Pro Desk'],
        '30310': ['Home Depot Garden Center'],
      }
    },
    {
      id: '7',
      name: 'Kroger',
      industry: 'Retail',
      union_status: true,
      union_name: 'UFCW Local 400',
      locations: {
        '45202': ['Kroger Store #123', 'Kroger Marketplace'],
        '45203': ['Kroger Fuel Center', 'Kroger Pharmacy'],
      }
    },
  ];

  const steps = [
    {
      title: 'Setup Your Workplace',
      description: 'Company, location, and union information',
      icon: Building,
    },
    {
      title: 'Complete Your Profile',
      description: 'Review details and add pay information (optional)',
      icon: User,
    },
  ];

  // Load existing onboarding progress
  useEffect(() => {
    loadOnboardingProgress();
  }, [user]);

  const loadOnboardingProgress = async () => {
    if (!user?.id) return;
    
    try {
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();
        
      if (profile && !error) {
        // Resume from saved data
        if (profile.company_name) setSelectedCompany(profile.company_name);
        if (profile.company_id) setSelectedCompanyId(profile.company_id);
        if (profile.zip_code) setSelectedZipCode(profile.zip_code);
        if (profile.work_location) setSelectedLocation(profile.work_location);
        if (profile.union_name) setDetectedUnion(profile.union_name);
        if (profile.has_union !== null) setHasUnion(profile.has_union);
        if (profile.pay_frequency) setPayFrequency(profile.pay_frequency);
        if (profile.hourly_rate) setHourlyRate(profile.hourly_rate.toString());
        if (profile.salary_amount) setSalaryAmount(profile.salary_amount.toString());
        
        // 🚀 SMART STEP DETECTION: Resume at appropriate screen
        if (profile.onboarding_step === 'completed' || profile.onboarding_completed) {
          // Already completed - shouldn't be here, but handle gracefully
          console.log('User has completed onboarding, redirecting to dashboard');
          onComplete();
          return;
        } else if (profile.onboarding_step === 'screen_2' || 
                   (profile.company_name && profile.zip_code && profile.work_location)) {
          // User completed Screen 1, resume at Screen 2
          console.log('🎯 Resuming onboarding at Screen 2 (Profile Completion)');
          setHasExistingProgress(true);
          setProgressMessage(`Welcome back! We saved your workplace info for ${profile.company_name}. Let's finish your profile.`);
          setCurrentStep(1);
        } else if (profile.company_name || profile.zip_code) {
          // User has some data but didn't complete Screen 1, stay on Screen 1
          console.log('🎯 Resuming onboarding at Screen 1 (Workplace Setup) with saved data');
          setHasExistingProgress(true);
          setProgressMessage('Welcome back! We saved some of your workplace information. Please complete the missing details.');
          setCurrentStep(0);
        } else {
          // New user, start fresh
          console.log('🆕 New user starting onboarding from beginning');
          setCurrentStep(0);
        }
      } else {
        // New user, start fresh
        console.log('🆕 New user starting onboarding from beginning');
        setCurrentStep(0);
      }
    } catch (error) {
      console.error('Error loading onboarding progress:', error);
      setCurrentStep(0); // Fallback to start
    }
  };

  const selectCompany = (companyName: string) => {
    const company = companies.find(c => c.name === companyName);
    if (!company) return;
    
    setSelectedCompany(companyName);
    setSelectedCompanyId(company.id);
    
    // Auto-detect union if applicable
    if (company.union_status) {
      setHasUnion(true);
      setDetectedUnion(company.union_name || '');
    } else {
      setHasUnion(false);
      setDetectedUnion('');
    }
  };

  const handleZipSubmit = () => {
    if (selectedZipCode.length === 5) {
      setShowLocationDropdown(true);
      setLocations(getLocationsForCompany(selectedCompany, selectedZipCode));
    }
  };

  const getLocationsForCompany = (companyName: string, zipCode: string): string[] => {
    if (!companyName || !zipCode) return [];
    
    const company = companies.find(c => c.name === companyName);
    if (!company || !company.locations) return [];
    
    const companyLocations = company.locations;
    return (companyLocations as any)[zipCode] || [];
  };

  const isNextButtonEnabled = () => {
    if (currentStep === 0) {
      // Screen 1: Require company and ZIP code
      return selectedCompany && selectedZipCode.length === 5;
    }
    // Screen 2: Always allow completion (pay info is optional)
    return true;
  };

  const showErrorAlert = (message: string) => {
    // In a real implementation, this would show a toast or alert
    alert(message);
  };

  const handleNext = async () => {
    // Validate Screen 1 before proceeding
    if (currentStep === 0) {
      if (!selectedCompany) {
        showErrorAlert('Please select your company');
        return;
      }
      if (selectedZipCode.length !== 5) {
        showErrorAlert('Please enter your ZIP code');
        return;
      }
      
      // 🚀 AUTO-SAVE SCREEN 1 PROGRESS before moving to Screen 2
      await savePartialProgress('screen_2');
    }
    
    setIsLoading(true);
    
    try {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        // Save final onboarding data before completing
        await saveOnboardingData();
        onComplete();
      }
    } catch (error) {
      console.error('Error proceeding to next step:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // 🚀 NEW: Auto-save partial progress (Screen 1 completion)
  const savePartialProgress = async (step: string) => {
    if (!user?.id) return;
    
    try {
      const partialData = {
        user_id: user.id,
        email: user.email,
        company_name: selectedCompany,
        company_id: selectedCompanyId,
        zip_code: selectedZipCode,
        work_location: selectedLocation,
        has_union: hasUnion,
        union_name: hasUnion ? detectedUnion : null,
        union_chapter: hasUnion ? selectedUnionChapter : null,
        onboarding_step: step,
        onboarding_completed: false,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('user_profiles')
        .upsert(partialData);
        
      if (error) {
        console.error('Error saving partial progress:', error);
      } else {
        console.log('✅ Partial progress saved successfully for step:', step);
      }
    } catch (error) {
      console.error('⚠️ Error saving partial progress:', error);
    }
  };

  // CRITICAL: Save onboarding data to Supabase database
  const saveOnboardingData = async () => {
    if (!user?.id) return;
    
    try {
      const onboardingData = {
        user_id: user.id,
        email: user.email,
        company_name: selectedCompany,
        company_id: selectedCompanyId,
        zip_code: selectedZipCode,
        work_location: selectedLocation,
        has_union: hasUnion,
        union_name: hasUnion ? detectedUnion : null,
        union_chapter: hasUnion ? selectedUnionChapter : null,
        pay_frequency: payFrequency,
        hourly_rate: payFrequency !== 'salary' ? parseFloat(hourlyRate) || null : null,
        salary_amount: payFrequency === 'salary' ? parseFloat(salaryAmount) || null : null,
        onboarding_completed: true,
        onboarding_completed_at: new Date().toISOString(),
        onboarding_step: 'completed'
      };

      const { error } = await supabase
        .from('user_profiles')
        .upsert(onboardingData);
        
      if (error) {
        console.error('Error saving onboarding data:', error);
      } else {
        console.log('✅ Onboarding data saved successfully:', onboardingData);
        
        // Save to localStorage for backward compatibility
        localStorage.setItem('clockit-onboarded', 'true');
      }
    } catch (error) {
      console.error('⚠️ Error saving onboarding data:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-accent/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* App Logo and Title - MATCHING LOGIN SCREEN */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center">
            <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center shadow-lg">
              <User className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold">Welcome to Clock-It</h1>
          <p className="text-muted-foreground">
            Let's set up your worker profile
          </p>
        </div>

        {/* Progress Recovery Message */}
        {hasExistingProgress && progressMessage && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              {progressMessage}
            </AlertDescription>
          </Alert>
        )}

        {/* Onboarding Card - MATCHING LOGIN SCREEN STYLE */}
        <Card className="clock-it-card">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              {React.createElement(steps[currentStep].icon, { className: "h-5 w-5 text-primary" })}
              {steps[currentStep].title}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {steps[currentStep].description}
            </p>
            
            {/* Progress indicator */}
            <div className="w-full bg-muted rounded-full h-2 mt-4">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300" 
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
          {currentStep === 0 ? (
            // Screen 1: Workplace Setup
            <div className="space-y-6">
              {/* Company Selection */}
              <div className="space-y-2">
                <Label htmlFor="company" className="text-sm font-medium text-gray-700">
                  Company <span className="text-red-500">*</span>
                </Label>
                <Select value={selectedCompany} onValueChange={selectCompany}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select your company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.name}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* ZIP Code Input */}
              <div className="space-y-2">
                <Label htmlFor="zipcode" className="text-sm font-medium text-gray-700">
                  ZIP Code <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="zipcode"
                  type="text"
                  placeholder="Enter your work ZIP code"
                  value={selectedZipCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setSelectedZipCode(value);
                    if (value.length === 5) {
                      handleZipSubmit();
                    }
                  }}
                  maxLength={5}
                  className="w-full"
                />
              </div>

              {/* Location Dropdown */}
              {showLocationDropdown && locations.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="location" className="text-sm font-medium text-gray-700">
                    Work Location
                  </Label>
                  <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select specific location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((location, index) => (
                        <SelectItem key={index} value={location}>
                          {location}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Union Information */}
              {detectedUnion && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Union Information</Label>
                  <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <Checkbox 
                      id="union" 
                      checked={hasUnion}
                      onCheckedChange={(checked) => setHasUnion(checked as boolean)}
                    />
                    <Label htmlFor="union" className="text-sm text-blue-700">
                      Member of {detectedUnion}
                    </Label>
                  </div>
                  <p className="text-xs text-gray-500">
                    Auto-detected based on your company
                  </p>
                </div>
              )}
            </div>
          ) : (
            // Screen 2: Profile Completion
            <div className="space-y-6">
              {/* Confirmation Section */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700">Your Information</Label>
                <div className="p-4 bg-gray-50 rounded-lg border space-y-3">
                  {selectedCompany && (
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4 text-blue-600" />
                      <div>
                        <p className="text-xs text-gray-500">Company</p>
                        <p className="text-sm font-medium text-gray-900">{selectedCompany}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedZipCode && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-blue-600" />
                      <div>
                        <p className="text-xs text-gray-500">ZIP Code</p>
                        <p className="text-sm font-medium text-gray-900">{selectedZipCode}</p>
                      </div>
                    </div>
                  )}
                  
                  {selectedLocation && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-blue-600" />
                      <div>
                        <p className="text-xs text-gray-500">Location</p>
                        <p className="text-sm font-medium text-gray-900">{selectedLocation}</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-500">Union Status</p>
                      <p className="text-sm font-medium text-gray-900">
                        {hasUnion ? (detectedUnion || 'Union Member') : 'Not Unionized'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pay Information Section */}
              <div className="space-y-4">
                <Label className="text-sm font-medium text-gray-700">
                  Pay Information (Optional)
                </Label>
                <p className="text-xs text-gray-500 italic">
                  Adding pay information helps us calculate your earnings and track your financial progress.
                </p>
                
                {/* Pay Frequency */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-600">Pay Frequency</Label>
                  <div className="flex flex-wrap gap-2">
                    {['weekly', 'bi-weekly', 'monthly', 'salary'].map((freq) => (
                      <Button
                        key={freq}
                        type="button"
                        variant={payFrequency === freq ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPayFrequency(freq)}
                        className="capitalize"
                      >
                        {freq.replace('-', ' ')}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Pay Amount */}
                <div className="space-y-2">
                  <Label htmlFor="payAmount" className="text-sm font-medium text-gray-600">
                    {payFrequency === 'salary' ? 'Annual Salary' : 'Hourly Rate'}
                  </Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      id="payAmount"
                      type="number"
                      placeholder={payFrequency === 'salary' ? 'Annual salary' : 'Hourly rate'}
                      value={payFrequency === 'salary' ? salaryAmount : hourlyRate}
                      onChange={(e) => {
                        if (payFrequency === 'salary') {
                          setSalaryAmount(e.target.value);
                        } else {
                          setHourlyRate(e.target.value);
                        }
                      }}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons - MATCHING LOGIN SCREEN STYLE */}
          <div className="flex justify-between items-center pt-4">
            {currentStep > 0 ? (
              <Button 
                variant="outline" 
                onClick={handleBack}
                className="flex items-center gap-2"
                disabled={isLoading}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            ) : (
              <div />
            )}
            
            <Button 
              onClick={handleNext}
              disabled={!isNextButtonEnabled() || isLoading}
              className="flex items-center gap-2"
            >
              {currentStep === 0 ? 'Continue' : currentStep === steps.length - 1 ? 'Complete Setup' : 'Next'}
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
}